import { faCopy, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import React, { useContext, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import { EActionItem, IPlansRoot } from "../../config/interfaces";
import FASvgIcon from "../FASvgIcon";
import PriceDisplay from "../PriceDisplay";
import { AFPlansTableHeadings } from "../TableHeaders";
import { ConfirmationModal } from "../modals";
import RowActionOverlay from "../overlay/RowActionOverlay";
import TablePagination from "../pagination/TablePagination";
import NoRecordFound from "../placeholders/NoRecordFound";

type Props = {
  assistedFilingsData: IPlansRoot[];
  pageCount: number;
  forcePage: number;
  showDeleteModal: IPlansRoot | null;
  handleOnPageChange: (event: any) => void;
  setShowDeleteModal: (event: any) => void;
  handleDelete: () => void;
  noRecords: boolean;
  hideColumns?: string[];
  isPerformingAction?: EActionItem;
};

const AFPlansTable: React.FC<Props> = (props: Props) => {
  const {
    assistedFilingsData,
    pageCount,
    forcePage,
    showDeleteModal,
    setShowDeleteModal,
    handleOnPageChange,
    noRecords = false,
    hideColumns,
    handleDelete,
    isPerformingAction
  } = props;

  const navigate = useNavigate();
  const { rulesSvc } = useContext(AppRulesContext);

  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset the copied state after 2 seconds
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <>
      <Card className="table-card">
        <Card.Body>
          <Table
            hover={noRecords ? false : rulesSvc.canAccess(rulesSvc.plansList) ? true : true}
            responsive
          >
            <thead>
              <tr>
                {AFPlansTableHeadings.map(
                  (_heading, index) =>
                    !hideColumns?.includes(AFPlansTableHeadings[index].title) && (
                      <th
                        className={`${_heading.classPrefix}${_heading.size}`}
                        key={`filing-th-${index}`}
                      >
                        {_heading.title}
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {!noRecords ? (
                <>
                  {assistedFilingsData.map((assistedFiling, index) => (
                    <tr
                      key={`assistedFiling-${index}`}
                      onClick={() =>
                        rulesSvc.canAccess(rulesSvc.plansList)
                          ? navigate(`/plans/assisted-filings/edit/${assistedFiling.uuid}`)
                          : null
                      }
                      className={classNames(!assistedFiling?.publishedAt && "bg-dangerLight")}
                    >
                      {!hideColumns?.includes(AFPlansTableHeadings[0].title) && (
                        <td className="text-capitalize">{assistedFiling?.title}</td>
                      )}
                      {!hideColumns?.includes(AFPlansTableHeadings[1].title) && (
                        <td
                          className={classNames(
                            "fw-bold",
                            assistedFiling?.publishedAt ? "text-success" : "text-danger"
                          )}
                        >
                          {assistedFiling?.publishedAt ? "Published" : "Unpublished"}
                        </td>
                      )}
                      {!hideColumns?.includes(AFPlansTableHeadings[2].title) && (
                        <td>
                          <PriceDisplay price={assistedFiling?.pricingTitle || 0} />
                        </td>
                      )}
                      {!hideColumns?.includes(AFPlansTableHeadings[1].title) && (
                        <td
                          className={classNames(
                            "fw-bold",
                            text ==
                              `${window.location.origin}/itr-filings/assisted/${assistedFiling?.uuid}`
                              ? "text-success"
                              : "text-black"
                          )}
                          onClick={(e) => {
                            handleCopy(
                              `${window.location.origin}/itr-filings/assisted/${assistedFiling?.uuid}`
                            );
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <span
                            style={{
                              display: "block",
                              width: "18px",
                              height: "18px",
                              marginLeft: 10
                            }}
                          >
                            <FASvgIcon icon={faCopy} />
                          </span>
                        </td>
                      )}
                      {!hideColumns?.includes(AFPlansTableHeadings[3].title) && (
                        <td
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <RowActionOverlay
                            popoverEvents={[
                              {
                                icon: faPen,
                                title: "Edit",
                                action: () =>
                                  navigate(`/plans/assisted-filings/edit/${assistedFiling.uuid}`),
                                actionRules: rulesSvc.updatePlan
                              },
                              {
                                icon: faTrash,
                                title: "Delete",
                                btnClass: "text-danger",
                                action: () => setShowDeleteModal(() => assistedFiling),
                                actionRules: rulesSvc.deletePlan
                              }
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ) : (
                <NoRecordFound colspan={AFPlansTableHeadings.length} />
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {!noRecords && (
        <TablePagination
          pageCount={pageCount}
          pageOffset={forcePage}
          handleOnPageChange={handleOnPageChange}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          showModal={!!showDeleteModal}
          onYesBtnClick={handleDelete}
          onNoBtnClick={() => {
            setShowDeleteModal(null);
          }}
          title="Please confirm"
          message={`Are you sure you want to  delete "${showDeleteModal?.title}"?`}
          onYesBtnText="Confirm"
          onNoBtnText="Cancel"
          isProcessing={isPerformingAction === EActionItem.DELETE_AF_PLAN}
        />
      )}
    </>
  );
};

export default AFPlansTable;
